import ArticalWrap from "/Component/Promotions/Keyword/ArticalWrap.js";
import Sidebar from "/Component/Shop/Sidebar.js";
import DestinationTopNav from "/Component/Promotions/Keyword/DestinationTopNav.js";
import TopBanner from "/Component/Promotions/Keyword/TopBanner.js";
import FilterDialog from "/Component/Promotions/Keyword/popup/FilterDialog.js";
import ProductHistoryDialog from "/Component/Promotions/Keyword/popup/ProductHistoryDialog.js";

import {
  scrollToElement,
  initializeFilterSync,
} from "/Services/shop/scrollService.js";

import _ from "/Scripts/lodash.js";
import flatpickr from "/Scripts/flatpickr.js";
import "/Scripts/dragscroll.min.js";
import "/Services/shop/apiService.js";
import "/Services/Promotions/Keyword/keywordService.js";
import "/Services/Promotions/Keyword/transService.js";
import "/Services/Promotions/Keyword/filterService.js";
import flatpickrService from "/Services/shop/flatpickr.js";

import {
  createApp,
  ref,
  reactive,
  computed,
  onMounted,
  provide,
  nextTick,
  watch,
  onUnmounted,
} from "/Scripts/vue.esm-browser.js";

window._ = window._ || _;
window.flatpickr = window.flatpickr || flatpickr;
window.flatpickrService = window.flatpickrService || flatpickrService;

const bootstrap = window.__CLEARANCE_SALE_BOOTSTRAP__;
if (!bootstrap) {
  throw new Error("Missing ClearanceSale bootstrap data.");
}

const apiClient = window.apiClient;
const keywordClient = window.keywordClient;
const transService = window.transService;
const filterService = window.filterService;

if (!apiClient || !keywordClient || !transService || !filterService) {
  throw new Error(
    "Missing ClearanceSale global services. Expected apiClient, keywordClient, transService, and filterService.",
  );
}

const arraysEqual = (arr1, arr2) => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
  if (arr1.length !== arr2.length) return false;
  return arr1.every((val, index) => val === arr2[index]);
};
const pipeJoin = (value) => _.join(Array.from(value), "|");
const pipeSplit = (value) => _.split(value, "|");

createApp({
  components: {
    ArticalWrap,
    Sidebar,
    DestinationTopNav,
    FilterDialog,
    ProductHistoryDialog,
    TopBanner,
  },
  setup() {
    const promCode = ref(bootstrap.promotionCode);
    const compId = ref(bootstrap.compId);
    const usrId = ref(bootstrap.usrId);
    const data = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const airports = ref([]);
    const user = ref(bootstrap.user);
    const mobileFilterPopup = ref(false);
    const historyDialogPopup = ref(false);
    const web = ref(bootstrap.webDesignUse);
    const historyBaseData = ref([]);
    const dept = ref("");
    dept.value =
      new URL(location.href).searchParams.get("dept")?.toUpperCase() ?? "";
    const companyInfo = ref([]);
    const filters = reactive({
      priceRange: [0, 10000000],
      dateRange: ["2025/01/01", "2025/12/31"],
      defaultPriceRange: [0, 10000000],
      defaultDateRange: ["2025/01/01", "2025/12/31"],
      groupStatus: {
        guaranteed: false,
        forming: false,
      },
      selectedWeekdays: new Set([]),
      dayRanges: [
        { rangeStart: 1, rangeEnd: 2, active: false },
        { rangeStart: 3, rangeEnd: 5, active: false },
        { rangeStart: 5, rangeEnd: 7, active: false },
        { rangeStart: 7, rangeEnd: 10, active: false },
        { rangeStart: 10, rangeEnd: 0, active: false },
      ],
      featureTags: new Set(),
      selectedAirlines: new Set(),
    });
    const sorts = ref({
      date: {
        active: false,
        sort: "asc",
      },
      price: {
        active: true,
        sort: "asc",
      },
      day: {
        active: false,
        sort: "asc",
      },
    });
    const groupShowType = ref("horizontal");
    const filterCount = ref(0);
    const compareGroups = ref([]);
    const isCompareUpdating = ref(false);
    const promotionData = ref([]);
    const isMounted = ref(false);
    const isFetchFinished = ref(false);
    const shouldTriggerDestinationOnMount = ref(false);
    const getHistoryData = async (mainGroupCode) => {
      historyBaseData.value =
        await apiClient.getHistoryByGroupCode(mainGroupCode);
      const order = pipeSplit(mainGroupCode);
      historyBaseData.value = _.sortBy(historyBaseData.value, (item) =>
        order.indexOf(item.mainGroupCode),
      );
    };
    const historyData = computed(() => {
      const data = transService.transGroupStatus(historyBaseData.value);
      return data.slice(0, 10);
    });
    const compareGroupCodeSet = computed(() => {
      return new Set(_.map(compareGroups.value, "groupCode"));
    });
    const isCompareSelected = (groupCode) => {
      return compareGroupCodeSet.value.has(groupCode);
    };
    const syncCompareSidebar = async () => {
      try {
        await UpdateCompareList(compareGroups.value);
      } catch (err) {
        console.error("更新比較清單側欄時發生錯誤:", err);
      }
    };
    const loadCompareGroups = async ({ syncSidebar = true } = {}) => {
      compareGroups.value = (await GetGroups()) || [];
      if (syncSidebar) {
        await syncCompareSidebar();
      }
    };
    const toggleCompare = async (groupCode, event) => {
      event?.stopPropagation();
      event?.preventDefault();
      if (!groupCode || isCompareUpdating.value) return;

      isCompareUpdating.value = true;
      try {
        if (isCompareSelected(groupCode)) {
          await RemoveGroup(groupCode);
        } else {
          await AddGroup(groupCode);
        }
        await loadCompareGroups();
      } catch (err) {
        console.error("更新比較清單時發生錯誤:", err);
      } finally {
        isCompareUpdating.value = false;
      }
    };
    provide("mobileFilterPopup", mobileFilterPopup);
    provide("historyDialogPopup", historyDialogPopup);
    provide("web", web);
    provide("getHistoryData", getHistoryData);
    provide("historyBaseData", historyBaseData);
    provide("historyData", historyData);
    provide("loading", loading);
    provide("compareGroups", compareGroups);
    provide("isCompareSelected", isCompareSelected);
    provide("toggleCompare", toggleCompare);
    provide("isCompareUpdating", isCompareUpdating);
    provide("groupShowType", groupShowType);
    provide("sorts", sorts);
    provide("filterCount", filterCount);
    provide("companyInfo", companyInfo);
    provide("compId", compId);
    provide("usrId", usrId);
    provide("dept", dept);

    const resetAllFilters = () => {
      const groups = _.flatMap(data.value, (item) => item.groups || []);
      const minPriceGroup = _.minBy(groups, "sitePrice");
      const maxPriceGroup = _.maxBy(groups, "sitePrice");
      const minDateGroup = _.minBy(groups, "leaveDate");
      const maxDateGroup = _.maxBy(groups, "leaveDate");

      const minPrice = minPriceGroup?.sitePrice ?? 0;
      const maxPrice = maxPriceGroup?.sitePrice ?? 168600;
      const minDate = minDateGroup?.leaveDate ?? "2025/01/01";
      const maxDate = maxDateGroup?.leaveDate ?? "2025/12/31";

      filters.priceRange = [minPrice, maxPrice];
      filters.dateRange = [minDate, maxDate];
      filters.defaultPriceRange = [minPrice, maxPrice];
      filters.defaultDateRange = [minDate, maxDate];
      filters.groupStatus.guaranteed = false;
      filters.groupStatus.forming = false;
      filters.selectedWeekdays.clear();

      _.forEach(filters.dayRanges, (item) => {
        item.active = false;
      });
      filters.featureTags.clear();
      filters.selectedAirlines.clear();
    };

    const updateFilters = (newFilters) => {
      filters.priceRange = newFilters.priceRange;
      filters.dateRange = newFilters.dateRange;
      filters.groupStatus = newFilters.groupStatus;
      filters.selectedWeekdays = newFilters.selectedWeekdays;
      filters.dayRanges = newFilters.dayRanges;
      filters.featureTags = newFilters.featureTags;
      filters.selectedAirlines = newFilters.selectedAirlines;
    };

    const mainGroupCancelRequest = ref(null);
    const runMountedDomInitializers = () => {
      if (!isMounted.value || !isFetchFinished.value) return;
      nextTick(() => {
        initializeFilterSync();
      });
    };

    const triggerDestinationFromURL = () => {
      if (!isMounted.value) {
        shouldTriggerDestinationOnMount.value = true;
        return;
      }
      const url = new URL(window.location.href);
      const destination = url.searchParams.get("location");
      if (!destination) return;

      let retryCount = 0;
      const maxRetries = 40;
      const retryDelay = 200;

      const tryClickDestination = () => {
        const anchorButtons = document.querySelectorAll(
          ".anchor_btn:not(.is-special),.anchor_popup>li",
        );

        if (anchorButtons.length > 0) {
          const targetButton = _.find(
            anchorButtons,
            (btn) => btn.textContent.trim() === destination,
          );

          if (targetButton) {
            targetButton.click();
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(tryClickDestination, retryDelay);
          }
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(tryClickDestination, retryDelay);
        }
      };

      tryClickDestination();
    };

    const loadClearanceData = async () => {
      try {
        let dataResource = "/Content/ClearanceSale/data.json";
        if (dept.value) {
          switch (dept.value) {
            case "TPE":
            case "TXG":
            case "KHH":
              dataResource = `/Content/ClearanceSale/data_${dept.value}.json`;
              break;
          }
        }
        const response = await fetch(dataResource);
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        const data = await response.json();
        return data;
      } catch (err) {
        console.error("讀取 JSON 時發生錯誤:", err);
        return [];
      }
    };

    const fetchData = async (afterDataReady) => {
      try {
        loading.value = true;
        const [airportsData, companyData] = await Promise.all([
          apiClient.getAirports(),
          apiClient.getCompanyInfo(bootstrap.webDesignUse),
        ]);

        airports.value = airportsData;
        companyInfo.value = companyData;
        const cancelRequest = keywordClient.getMainGroupInfoByWebSiteSSE(
          promCode.value,
          setDataSource,
          () => {
            resetAllFilters();
            initFiltersFromURL();
            loading.value = false;
            if (afterDataReady) {
              afterDataReady();
            }
          },
        );

        mainGroupCancelRequest.value = cancelRequest;
        await loadCompareGroups();
      } catch (err) {
        error.value = err.message || "獲取數據時出錯";
        console.error(error.value);
      } finally {
        isFetchFinished.value = true;
        runMountedDomInitializers();
      }
    };

    onUnmounted(() => {
      if (mainGroupCancelRequest.value) {
        mainGroupCancelRequest.value();
      }
    });

    const setDataSource = (mainGroupData, airlines) => {
      transService.convertAirlinesToDictionary(airlines);

      data.value = transService.splitOpenDate([...mainGroupData]);
      data.value = transService.transDeparture(data.value, airports.value);

      if (dept.value) {
        const departureByDept = {
          TPE: "桃園",
          TXG: "台中",
          KHH: "高雄",
        };
        const departure = departureByDept[dept.value];
        if (departure) {
          data.value = _.filter(
            data.value,
            (item) => item.departure === departure,
          );
        }
      }

      data.value = transService.transGroupStatus(data.value);
      data.value = transService.sortGroups(data.value);
      data.value = transService.setPromotions(data.value, promotionData.value);
    };

    const LOADING_SKELETON = {
      ITEM_COUNT: 5,
      AREA_COUNT: 5,
      HASHTAG_COUNT: 6,
    };
    const createLoadingSkeleton = () => ({
      departureName: "",
      areas: Array.from({ length: LOADING_SKELETON.AREA_COUNT }, (_, index) => {
        if (index === 0) {
          return {
            area: "",
            promotions: [
              {
                promotion: "",
                promotionData: null,
                subAreas: [
                  {
                    subAreaName: "",
                    items: Array.from(
                      {
                        length: LOADING_SKELETON.ITEM_COUNT,
                      },
                      () => ({
                        groups: [{}],
                        hashTagList: Array(LOADING_SKELETON.HASHTAG_COUNT).fill(
                          "",
                        ),
                      }),
                    ),
                  },
                ],
              },
            ],
          };
        }
        return {
          area: " ".repeat(index),
          promotions: [],
        };
      }),
    });

    const filteredData = computed(() => {
      if (!data.value) return [];
      return filterService.applyFilters(data.value, filters, loading.value);
    });
    const filteredDataCount = computed(() => {
      const groupTypeData = _.filter(
        filteredData.value,
        (item) => item.type === "Group",
      );

      if (groupShowType.value === "uni") {
        return _.sumBy(groupTypeData, (item) => item.groups.length);
      }

      return _.filter(groupTypeData, (item) => item.groups.length > 0)
        .length;
    });
    provide("filteredDataCount", filteredDataCount);

    const filteredDataByGroup = computed(() => {
      if (loading.value) {
        return [createLoadingSkeleton()];
      }
      if (!filteredData.value) return [];
      return transService.createGroupedStructure(
        filteredData.value,
        groupShowType.value,
        sorts.value,
      );
    });

    const updateURLFromFilters = (currentFilters, allProductBaseData) => {
      const currentParams = new URLSearchParams(window.location.search);
      const baseUrl = window.location.origin + window.location.pathname;

      if (
        !arraysEqual(currentFilters.dateRange, currentFilters.defaultDateRange)
      ) {
        const [startDate, endDate] = currentFilters.dateRange;
        currentParams.set("startdate", startDate);
        currentParams.set("enddate", endDate);
      } else {
        currentParams.delete("startdate");
        currentParams.delete("enddate");
      }

      if (
        !arraysEqual(
          currentFilters.priceRange,
          currentFilters.defaultPriceRange,
        )
      ) {
        const [priceLow, priceHigh] = currentFilters.priceRange;
        currentParams.set("pricel", priceLow);
        currentParams.set("priceh", priceHigh);
      } else {
        currentParams.delete("pricel");
        currentParams.delete("priceh");
      }

      if (currentFilters.featureTags && currentFilters.featureTags.size > 0) {
        currentParams.set("featuretags", pipeJoin(currentFilters.featureTags));
      } else {
        currentParams.delete("featuretags");
      }

      if (
        currentFilters.selectedAirlines &&
        currentFilters.selectedAirlines.size > 0
      ) {
        currentParams.set("airlines", pipeJoin(currentFilters.selectedAirlines));
      } else {
        currentParams.delete("airlines");
      }

      if (
        currentFilters.selectedWeekdays &&
        currentFilters.selectedWeekdays.size > 0
      ) {
        currentParams.set("weekdays", pipeJoin(currentFilters.selectedWeekdays));
      } else {
        currentParams.delete("weekdays");
      }

      const activeDayRanges = _.compact(
        _.map(currentFilters.dayRanges, (range, index) =>
          range.active ? String(index) : "",
        ),
      );

      if (activeDayRanges.length > 0) {
        currentParams.set("dayranges", activeDayRanges.join("|"));
      } else {
        currentParams.delete("dayranges");
      }

      const activeStatuses = _.keys(
        _.pickBy(currentFilters.groupStatus, (value) => value === true),
      );

      if (activeStatuses.length > 0) {
        currentParams.set("status", activeStatuses.join("|"));
      } else {
        currentParams.delete("status");
      }

      if (allProductBaseData) {
        const allProductBaseDataString = { ...allProductBaseData };
        delete allProductBaseDataString.data;
        currentParams.set(
          "allProductBaseData",
          JSON.stringify(allProductBaseDataString),
        );
      } else {
        currentParams.delete("allProductBaseData");
      }

      const newUrl = `${baseUrl}?${currentParams.toString()}`;
      window.history.replaceState({}, "", newUrl);
    };

    const initFiltersFromURL = () => {
      const params = new URLSearchParams(window.location.search);

      const start = params.get("startdate");
      const end = params.get("enddate");
      if (start && end) {
        filters.dateRange = [start, end];
      }

      const pricel = params.get("pricel");
      const priceh = params.get("priceh");
      if (pricel && priceh) {
        filters.priceRange = [parseInt(pricel, 10), parseInt(priceh, 10)];
      }

      const featureTags = params.get("featuretags");
      if (featureTags) {
        filters.featureTags = new Set(pipeSplit(featureTags));
      }

      const airlines = params.get("airlines");
      if (airlines) {
        filters.selectedAirlines = new Set(pipeSplit(airlines));
      }

      const weekdays = params.get("weekdays");
      if (weekdays) {
        filters.selectedWeekdays = new Set(_.map(pipeSplit(weekdays), Number));
      }

      const dayranges = params.get("dayranges");
      if (dayranges) {
        _.forEach(_.map(pipeSplit(dayranges), Number), (i) => {
          if (filters.dayRanges[i]) {
            filters.dayRanges[i].active = true;
          }
        });
      }

      const status = params.get("status");
      if (status) {
        const keys = pipeSplit(status);
        filters.groupStatus.guaranteed = _.includes(keys, "guaranteed");
        filters.groupStatus.forming = _.includes(keys, "forming");
      }
    };
    const scrollToFirstDestination = async () => {
      await nextTick();
      const btn = document.querySelector(
        ".anchor_list > .anchor_btn:nth-child(1)",
      );
      if (btn) {
        btn.click();
        return;
      }

      const main = document.querySelector("#main");
      if (main) {
        scrollToElement(main, {
          padding: 20,
          behavior: "smooth",
          includeNavHeight: true,
          includeMobileHeader: true,
          enableFilterSync: false,
        });
        return;
      }

      console.warn("未找到 #main 元素");
    };

    watch(
      () => filterCount.value,
      async (newFilters) => {
        if (newFilters == 0) {
          const url = new URL(window.location.href);
          const dept = url.searchParams.get("dept");

          url.search = "";

          if (dept) {
            url.searchParams.set("dept", dept);
          }

          window.history.replaceState(
            {},
            "",
            url.pathname + url.search + url.hash,
          );
        } else {
          await scrollToFirstDestination();
        }
      },
      { deep: true },
    );

    const initializeData = async () => {
      promotionData.value = await loadClearanceData();
      fetchData(() => triggerDestinationFromURL());
    };
    initializeData();

    onMounted(() => {
      if (dept.value) {
        document.body.classList.add(`${dept.value}`);
      }
      isMounted.value = true;
      runMountedDomInitializers();
      if (shouldTriggerDestinationOnMount.value) {
        shouldTriggerDestinationOnMount.value = false;
        triggerDestinationFromURL();
      }
    });

    return {
      data,
      promotionData,
      loading,
      error,
      filters,
      resetAllFilters,
      fetchData,
      updateFilters,
      filteredData,
      filteredDataByGroup,
      promCode,
      user,
      dept,
    };
  },
}).mount("#app");
